import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, RefreshCw, XCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/odds";
import { useAuth } from "@/context/AuthContext";
import { TierBadge } from "@/components/TierBadge";
import { ReferralPanel } from "@/components/ReferralPanel";

const PLAN_NAMES = { basic_monthly: "Basic · Monthly", basic_yearly: "Basic · Yearly", premium_monthly: "Premium · Monthly", premium_yearly: "Premium · Yearly" };

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/billing").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const { data } = await fn();
      toast.success(data.message || ok);
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const cancel = () => run("cancel", () => api.post("/billing/cancel"), "Subscription cancelled");
  const resume = () => run("resume", () => api.post("/billing/resume"), "Subscription resumed");
  const switchTo = (lookup_key) => run(lookup_key, () => api.post("/billing/switch", { lookup_key }), "Plan switched");
  const portal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };

  const sub = data?.subscription;
  const alt = Object.keys(PLAN_NAMES).filter((k) => k !== data?.lookup_key);

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8" data-testid="billing-page">
      <p className="label-caps text-emerald-400">Account</p>
      <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Billing, Plan & Referrals</h1>

      <div className="mt-8 glass-card p-6" data-testid="billing-current-plan">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="label-caps">Current plan</p>
            <div className="mt-2 flex items-center gap-3"><span className="font-display text-3xl font-black uppercase text-white" data-testid="billing-tier">{user.tier}</span><TierBadge tier={user.tier} /></div>
            <p className="mt-1 text-sm text-slate-400" data-testid="billing-plan-detail">
              {data?.lifetime ? "Lifetime Premium — complimentary, never expires." : sub ? `${PLAN_NAMES[sub.lookup_key] || sub.lookup_key} · ${money(sub.amount)}/${sub.interval} · ${sub.cancel_at_period_end ? "cancels" : "renews"} ${new Date(sub.current_period_end).toLocaleDateString()}` : user.tier === "free" ? "Free forever. Upgrade any time." : "Complimentary plan (not billed through Stripe)."}
            </p>
            {data?.lifetime && <p data-testid="billing-lifetime-badge" className="mt-2 inline-block rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200">Lifetime access granted</p>}
            {sub?.cancel_at_period_end && <p data-testid="billing-cancel-notice" className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-200">Cancellation scheduled — you keep {user.tier} access until the period ends, then drop to Free automatically.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {user.tier === "free" && <Link to="/pricing" data-testid="billing-upgrade-link" className="btn-primary">Choose a plan</Link>}
            {user.tier !== "free" && !data?.lifetime && !sub?.cancel_at_period_end && <button data-testid="billing-cancel-btn" onClick={cancel} disabled={busy === "cancel"} className="btn-ghost !border-red-500/40 !text-red-300"><XCircle size={15} /> {busy === "cancel" ? "Cancelling…" : "Cancel plan"}</button>}
            {sub?.cancel_at_period_end && <button data-testid="billing-resume-btn" onClick={resume} disabled={busy === "resume"} className="btn-primary"><RefreshCw size={15} /> {busy === "resume" ? "Resuming…" : "Keep my plan"}</button>}
            {data?.managed_by_stripe && <button data-testid="billing-portal-btn" onClick={portal} disabled={busy === "portal"} className="btn-ghost"><CreditCard size={15} /> Payment method <ExternalLink size={13} /></button>}
          </div>
        </div>
      </div>

      {data?.managed_by_stripe && (
        <div className="mt-6 glass-card p-6" data-testid="billing-switch-panel">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Switch plan</h2>
          <p className="text-xs text-slate-500">Changes take effect immediately and are prorated on your next invoice.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {alt.map((k) => (
              <button key={k} data-testid={`billing-switch-${k}`} onClick={() => switchTo(k)} disabled={!!busy} className="glass-card flex items-center justify-between p-4 text-left hover:!border-emerald-500/60">
                <span className="font-semibold text-white">{PLAN_NAMES[k]}</span>
                <span className="font-mono-num text-sm text-slate-400">{k.startsWith("basic") ? (k.endsWith("monthly") ? "$19/mo" : "$159/yr") : k.endsWith("monthly") ? "$49/mo" : "$399/yr"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {data && !data.managed_by_stripe && !data.lifetime && user.tier !== "free" && (
        <p className="mt-6 text-sm text-slate-500" data-testid="billing-complimentary-note">This account's plan isn't billed through Stripe, so plan switching happens via <Link to="/pricing" className="text-emerald-400 underline">Pricing</Link> checkout.</p>
      )}
      <div className="mt-6"><ReferralPanel /></div>
    </main>
  );
}
